import { Plus } from "lucide-react";
import { Badge, Card, CardContent, CardHeader, CardTitle } from "@eduviaos/ui-kit";
import type { TemplateVariable } from "@/lib/emailTemplates/types";

interface VariablesSidebarProps {
  variables: TemplateVariable[];
  /** Insert `{{ name }}` at the cursor of the focused Monaco editor. */
  onInsert: (snippet: string) => void;
}

/**
 * Right-rail sidebar listing every catalogue variable with click-to-
 * insert behaviour. Required vars get a subtle indicator so the SA
 * sees at a glance which ones must be referenced somewhere in the
 * template before saving.
 */
export function VariablesSidebar({ variables, onInsert }: VariablesSidebarProps) {
  return (
    <Card className="h-fit">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">Available variables</CardTitle>
        <p className="text-muted-foreground text-xs">
          Click to insert at the cursor of the focused editor. Required variables must appear at
          least once across subject/HTML/text.
        </p>
      </CardHeader>
      <CardContent className="grid gap-1.5">
        {variables.map((v) => (
          <button
            key={v.name}
            type="button"
            onClick={() => onInsert(`{{ ${v.name} }}`)}
            className="bg-muted/30 hover:border-border hover:bg-muted/60 focus-visible:border-primary group grid gap-1 rounded-md border border-transparent px-2.5 py-2 text-left transition focus-visible:outline-none"
            title={v.description ?? v.label}
          >
            <div className="flex items-center justify-between gap-2">
              <code className="text-foreground text-xs font-medium">{`{{ ${v.name} }}`}</code>
              {v.required ? (
                <Badge variant="warn" className="text-[10px]">
                  Required
                </Badge>
              ) : (
                <Plus
                  className="text-muted-foreground size-3 opacity-0 transition group-hover:opacity-100"
                  aria-hidden="true"
                />
              )}
            </div>
            <p className="text-muted-foreground text-[11px] leading-tight">
              {v.label}
              {v.sample_value && (
                <>
                  {" "}
                  <span className="opacity-70">e.g. {v.sample_value}</span>
                </>
              )}
            </p>
          </button>
        ))}
      </CardContent>
    </Card>
  );
}
